import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CalendarOff, Link2, Loader2, Plus, RefreshCw, Trash2, Unplug } from "lucide-react";

import API from "../api";

export default function AvailabilityManager() {
  const [status, setStatus] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [form, setForm] = useState({ arrival: "", departure: "", status: "confirmed" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    fetch(`${API}/api/admin/google/status`, { credentials: "include" })
      .then((r) => r.json())
      .then(setStatus)
      .catch(() => setStatus({ configured: false, connected: false }));
    fetch(`${API}/api/admin/blocks`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => setBlocks(d.blocks || []))
      .catch(() => setBlocks([]));
  };

  useEffect(() => {
    load();
  }, []);

  const connect = async () => {
    try {
      const r = await fetch(`${API}/api/google/authorize`, { credentials: "include" });
      const data = await r.json();
      if (data.configured && data.authorization_url) {
        window.location.assign(data.authorization_url);
      } else {
        toast.error(data.message || "Google is not configured yet");
      }
    } catch {
      toast.error("Could not start the Google connection");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect the Google Calendar? The website will show availability as being set up.")) return;
    await fetch(`${API}/api/admin/google/disconnect`, { method: "POST", credentials: "include" });
    toast.success("Google Calendar disconnected");
    load();
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/google/sync`, { method: "POST", credentials: "include" });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || "Sync failed");
        return;
      }
      if (data.sync_status === "ok") {
        toast.success(`Synced — ${data.blocks} booking period${data.blocks === 1 ? "" : "s"} up to date`);
      } else {
        toast.error(data.last_error || "Sync failed — the connection needs attention");
      }
      load();
    } catch {
      toast.error("Sync failed");
    } finally {
      setBusy(false);
    }
  };

  const addBlock = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/blocks`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || "Could not add the block");
        return;
      }
      toast.success("Block added to your Google Calendar");
      setForm({ arrival: "", departure: "", status: "confirmed" });
      load();
    } catch {
      toast.error("Could not add the block");
    } finally {
      setBusy(false);
    }
  };

  const removeBlock = async (eventId) => {
    const r = await fetch(`${API}/api/admin/blocks/${eventId}`, { method: "DELETE", credentials: "include" });
    if (r.ok) {
      toast.success("Block removed");
      load();
    } else {
      toast.error("Could not remove the block");
    }
  };

  const fieldCls = "mt-2 w-full border border-ivory/25 bg-navy px-4 py-3 text-sm text-ivory focus:border-gold focus:outline-none";

  return (
    <div className="mt-8" data-testid="availability-manager">
      <h2 className="font-display text-3xl text-ivory">Availability &amp; Bookings</h2>
      <p className="mt-3 max-w-2xl text-sm font-light leading-relaxed text-ivory/70">
        Your Google Calendar drives the public availability. Manage bookings day-to-day in the Google Calendar app on
        your phone: a normal event is a <strong className="text-ivory">Confirmed</strong> booking, mark it{" "}
        <strong className="text-ivory">Free</strong> for a <strong className="text-ivory">Provisional</strong> hold,
        put <strong className="text-ivory">Maintenance</strong> in the title for a maintenance block, and delete an
        event to cancel it. This panel is your backup.
      </p>

      {status && (
        <div className="mt-6 border border-ivory/15 bg-navy-2 p-6" data-testid="google-status-card">
          {status.sync_status && status.sync_status !== "ok" && status.connected && (
            <div className="mb-4 flex items-start gap-3 border border-gold/50 bg-gold/10 p-4" data-testid="google-stale-warning">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <p className="text-sm font-light text-ivory/85">
                The calendar connection needs attention{status.last_error ? ` — ${status.last_error}` : ""}. The website
                is not showing unverified dates as definitely available. Reconnect below if it persists.
              </p>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CalendarOff className="h-6 w-6 text-gold" />
              <p className="text-base text-ivory" data-testid="google-status-line">
                {!status.connected
                  ? status.message || (status.configured
                    ? "Ready to connect — one tap, then Google asks you to sign in"
                    : "Not set up yet — add your Google client id and secret in the website secrets")
                  : status.sync_status === "ok"
                    ? `Connected — ${status.blocks} booking period${status.blocks === 1 ? "" : "s"} in sync${status.last_sync_at ? ` · last checked ${new Date(status.last_sync_at).toLocaleString("en-IE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}`
                    : `Connection needs attention — ${status.sync_status}`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {status.connected && (
                <button
                  onClick={syncNow}
                  disabled={busy}
                  data-testid="google-sync-now-button"
                  className="flex items-center gap-2 border border-gold px-5 py-3.5 text-xs font-semibold uppercase tracking-widest2 text-gold-2 transition-colors hover:bg-gold hover:text-navy disabled:opacity-60"
                >
                  <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Sync Now
                </button>
              )}
              <button
                onClick={connect}
                data-testid="google-connect-button"
                className="flex items-center gap-2 bg-gold px-7 py-3.5 text-xs font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-gold-2"
              >
                <Link2 className="h-4 w-4" /> {status.connected ? "Reconnect" : "Connect Google Calendar"}
              </button>
              {status.connected && (
                <button
                  onClick={disconnect}
                  data-testid="google-disconnect-button"
                  className="flex items-center gap-2 border border-ivory/25 px-5 py-3 text-[11px] font-semibold uppercase tracking-widest2 text-ivory/80 hover:border-red-400 hover:text-red-400"
                >
                  <Unplug className="h-4 w-4" /> Disconnect
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {status && status.connected && (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <form onSubmit={addBlock} className="border border-ivory/15 bg-navy-2 p-6" data-testid="block-form">
            <h3 className="font-display text-xl text-ivory">Add a booking block</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-widest2 text-gold-2">Arrival</span>
                <input required type="date" value={form.arrival} onChange={(e) => setForm({ ...form, arrival: e.target.value })} data-testid="block-arrival-input" className={fieldCls} />
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-widest2 text-gold-2">Departure</span>
                <input required type="date" value={form.departure} onChange={(e) => setForm({ ...form, departure: e.target.value })} data-testid="block-departure-input" className={fieldCls} />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs font-semibold uppercase tracking-widest2 text-gold-2">Status</span>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="block-status-select" className={fieldCls}>
                  <option value="confirmed">Confirmed</option>
                  <option value="provisional">Provisional</option>
                  <option value="maintenance">Maintenance block</option>
                </select>
              </label>
            </div>
            <button type="submit" disabled={busy} data-testid="block-add-button" className="mt-5 flex items-center gap-2 bg-gold px-7 py-3 text-xs font-semibold uppercase tracking-widest2 text-navy hover:bg-gold-2 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add block
            </button>
          </form>

          <div className="border border-ivory/15 bg-navy-2 p-6" data-testid="block-list">
            <h3 className="font-display text-xl text-ivory">Upcoming booking periods</h3>
            <div className="mt-4 flex flex-col gap-2">
              {blocks.length === 0 && (
                <p className="text-sm font-light text-ivory/60">No blocks — every date shows as available.</p>
              )}
              {blocks.map((b) => (
                <div key={b.event_id} className="flex items-center justify-between gap-3 border border-ivory/15 px-4 py-3" data-testid="block-row">
                  <div>
                    <p className="text-sm text-ivory">
                      {b.start} → {b.end}
                    </p>
                    <p className="text-[11px] uppercase tracking-widest2 text-gold-2">{b.status}</p>
                  </div>
                  <button onClick={() => removeBlock(b.event_id)} data-testid={`block-delete-${b.event_id}`} className="border border-ivory/25 px-3 py-2 text-ivory/70 hover:border-red-400 hover:text-red-400" aria-label="Remove block">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={load} data-testid="blocks-refresh-button" className="mt-4 flex items-center gap-2 border border-ivory/25 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest2 text-ivory/80 hover:text-gold-2">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
          </div>
        </div>
      )}
    </div>
  );
}